import type { TeamReferenceStory } from '@rhythmic/db/types'
import type { CreateTeamReferenceStoryPayload } from '@rhythmic/types'
import { CreateTeamReferenceStorySchema, generateUuid, TeamReferenceStorySchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getTeamReferenceStoryCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:createReferenceStory')

export interface CreateReferenceStoryContext {
  teamId: string
}

function validateParams(payload: CreateTeamReferenceStoryPayload, context: CreateReferenceStoryContext) {
  const { story, estimate, description, created_by } = CreateTeamReferenceStorySchema.parse(payload)
  const { teamId } = context

  if (!teamId) {
    const error = new Error('Team context is not ready')
    logger.error('Unable to create reference story: missing team identifier')
    throw error
  }

  return {
    story,
    estimate,
    description,
    created_by,
    teamId,
  }
}

export default function handleCreateReferenceStory(
  payload: CreateTeamReferenceStoryPayload,
  context: CreateReferenceStoryContext,
): Transaction<TeamReferenceStory> {
  try {
    const { story, estimate, description, created_by, teamId } = validateParams(payload, context)

    const collection = getTeamReferenceStoryCollection(teamId)

    const newReferenceStory = TeamReferenceStorySchema.parse({
      id: generateUuid(),
      team_id: teamId,
      story,
      estimate,
      description: description ?? null,
      created_by: created_by ?? null,
      created_at: new Date(),
      updated_at: new Date(),
    })

    const transaction = collection.insert(newReferenceStory) as Transaction<TeamReferenceStory>

    logger.info('Reference story created via collection', {
      teamId,
      storyId: newReferenceStory.id,
    })

    return transaction
  } catch (error) {
    logger.error('createReferenceStory failed', { error })
    if (error instanceof Error) throw error
    throw new Error('Failed to create reference story')
  }
}
